import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  lmsGetClasses,
  lmsGetSchoolClasses,
  lmsGetSubjects,
  lmsGetClassSubjects,
  lmsGetChapters,
  lmsGetTopics,
  lmsGetMaterials,
  lmsGetCourses,
  lmsGetCourseById,
  lmsCreateClass,
  lmsCreateSubject,
  lmsCreateClassSubject,
  lmsCreateChapter,
  lmsCreateTopic,
  lmsDeleteMaterial,
} from "../api/lmsApi";

// ---- Async Thunks ----

export const fetchLmsSchoolClasses = createAsyncThunk(
  "lms/fetchSchoolClasses",
  async (schoolId, { rejectWithValue }) => {
    try {
      const res = await lmsGetSchoolClasses(schoolId);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch school classes");
    }
  }
);

export const fetchLmsClasses = createAsyncThunk(
  "lms/fetchClasses",
  async (_, { rejectWithValue }) => {
    try {
      const res = await lmsGetClasses();
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch classes");
    }
  }
);

export const fetchLmsSubjects = createAsyncThunk(
  "lms/fetchSubjects",
  async (schoolId, { rejectWithValue }) => {
    try {
      const res = await lmsGetSubjects(schoolId);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch subjects");
    }
  }
);

export const fetchLmsClassSubjects = createAsyncThunk(
  "lms/fetchClassSubjects",
  async (params, { rejectWithValue }) => {
    try {
      const res = await lmsGetClassSubjects(params);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch class-subjects");
    }
  }
);

export const fetchLmsChapters = createAsyncThunk(
  "lms/fetchChapters",
  async (params, { rejectWithValue }) => {
    try {
      const res = await lmsGetChapters(params);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch chapters");
    }
  }
);

export const fetchLmsTopics = createAsyncThunk(
  "lms/fetchTopics",
  async (params, { rejectWithValue }) => {
    try {
      const res = await lmsGetTopics(params);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch topics");
    }
  }
);

export const fetchLmsMaterials = createAsyncThunk(
  "lms/fetchMaterials",
  async (params, { rejectWithValue }) => {
    try {
      const res = await lmsGetMaterials(params);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch materials");
    }
  }
);

export const fetchLmsCourses = createAsyncThunk(
  "lms/fetchCourses",
  async (params, { rejectWithValue }) => {
    try {
      const res = await lmsGetCourses(params);
      return res.data.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch courses");
    }
  }
);

export const fetchLmsCourseById = createAsyncThunk(
  "lms/fetchCourseById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await lmsGetCourseById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch course");
    }
  }
);

// ---- Initial State ----
const initialState = {
  // Master Setup data
  classes:       [],   // all global classes
  schoolClasses: [],   // classes assigned to this school
  subjects:      [],
  classSubjects: [],
  chapters:      [],
  topics:        [],
  materials:     [],

  // Selected IDs (4-column panel)
  selectedClassId:        null,
  selectedSubjectId:      null,
  selectedClassSubjectId: null,
  selectedChapterId:      null,
  selectedTopicId:        null,

  // Courses
  courses:        [],
  selectedCourse: null,

  // UI state
  loading:           false,
  coursesLoading:    false,
  materialsLoading:  false,
  error:             null,
  uploadModalOpen:   false,
};

// ---- Slice ----
const lmsSlice = createSlice({
  name: "lms",
  initialState,
  reducers: {
    setSelectedClass(state, action) {
      state.selectedClassId        = action.payload;
      state.selectedClassSubjectId = null;
      state.selectedChapterId      = null;
      state.selectedTopicId        = null;
      // Do NOT clear chapters/topics/materials — they are fetched all-at-once
      // and filtered client-side in MasterSetup
    },
    setSelectedSubject(state, action) {
      state.selectedSubjectId      = action.payload;
      state.selectedClassSubjectId = null;
      state.selectedChapterId      = null;
      state.selectedTopicId        = null;
    },
    setSelectedClassSubject(state, action) {
      state.selectedClassSubjectId = action.payload;
      state.selectedChapterId      = null;
      state.selectedTopicId        = null;
    },
    setSelectedChapter(state, action) {
      state.selectedChapterId = action.payload;
      state.selectedTopicId   = null;
    },
    setSelectedTopic(state, action) {
      state.selectedTopicId = action.payload;
    },
    setUploadModalOpen(state, action) {
      state.uploadModalOpen = action.payload;
    },
    setSelectedCourse(state, action) {
      state.selectedCourse = action.payload;
    },
    clearLmsError(state) {
      state.error = null;
    },
    // Optimistic add after create
    addClassOptimistic(state, action) {
      state.classes.push(action.payload);
    },
    addSubjectOptimistic(state, action) {
      state.subjects.push(action.payload);
    },
    addChapterOptimistic(state, action) {
      state.chapters.push(action.payload);
    },
    addTopicOptimistic(state, action) {
      state.topics.push(action.payload);
    },
    removeMaterial(state, action) {
      state.materials = state.materials.filter((m) => m.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // School Classes
    builder
      .addCase(fetchLmsSchoolClasses.pending,   (state) => { state.loading = true; })
      .addCase(fetchLmsSchoolClasses.fulfilled, (state, { payload }) => { state.loading = false; state.schoolClasses = payload; })
      .addCase(fetchLmsSchoolClasses.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; });

    // Classes
    builder
      .addCase(fetchLmsClasses.pending,  (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLmsClasses.fulfilled,(state, { payload }) => { state.loading = false; state.classes = payload; })
      .addCase(fetchLmsClasses.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // Subjects
    builder
      .addCase(fetchLmsSubjects.pending,  (state) => { state.loading = true; })
      .addCase(fetchLmsSubjects.fulfilled,(state, { payload }) => { state.loading = false; state.subjects = payload; })
      .addCase(fetchLmsSubjects.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // Class-Subjects
    builder
      .addCase(fetchLmsClassSubjects.pending,  (state) => { state.loading = true; })
      .addCase(fetchLmsClassSubjects.fulfilled,(state, { payload }) => { state.loading = false; state.classSubjects = payload; })
      .addCase(fetchLmsClassSubjects.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // Chapters
    builder
      .addCase(fetchLmsChapters.pending,  (state) => { state.loading = true; })
      .addCase(fetchLmsChapters.fulfilled,(state, { payload }) => { state.loading = false; state.chapters = payload; })
      .addCase(fetchLmsChapters.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // Topics
    builder
      .addCase(fetchLmsTopics.pending,  (state) => { state.loading = true; })
      .addCase(fetchLmsTopics.fulfilled,(state, { payload }) => { state.loading = false; state.topics = payload; })
      .addCase(fetchLmsTopics.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // Materials
    builder
      .addCase(fetchLmsMaterials.pending,  (state) => { state.materialsLoading = true; })
      .addCase(fetchLmsMaterials.fulfilled,(state, { payload }) => { state.materialsLoading = false; state.materials = payload; })
      .addCase(fetchLmsMaterials.rejected, (state, { payload }) => { state.materialsLoading = false; state.error = payload; });

    // Courses
    builder
      .addCase(fetchLmsCourses.pending,  (state) => { state.coursesLoading = true; state.error = null; })
      .addCase(fetchLmsCourses.fulfilled,(state, { payload }) => { state.coursesLoading = false; state.courses = payload; })
      .addCase(fetchLmsCourses.rejected, (state, { payload }) => { state.coursesLoading = false; state.error = payload; });

    // Course by ID
    builder
      .addCase(fetchLmsCourseById.pending,  (state) => { state.loading = true; })
      .addCase(fetchLmsCourseById.fulfilled,(state, { payload }) => { state.loading = false; state.selectedCourse = payload; })
      .addCase(fetchLmsCourseById.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });
  },
});

export const {
  setSelectedClass,
  setSelectedSubject,
  setSelectedClassSubject,
  setSelectedChapter,
  setSelectedTopic,
  setUploadModalOpen,
  setSelectedCourse,
  clearLmsError,
  addClassOptimistic,
  addSubjectOptimistic,
  addChapterOptimistic,
  addTopicOptimistic,
  removeMaterial,
} = lmsSlice.actions;

export default lmsSlice.reducer;
